//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { ColumnInfo } from '../../models/custom'
import { Enums } from '../../models/common'
import { IReferenceType, IOpObject } from '../../models/util/interfaces'
import { OpPackage, OpDataTypeInGroup, OpView, OpViewColumn, OpDepositoryElement } from '../../models/core'

import { ColumnModel } from '../../modules/api/api.models'
import { CreateEditInitParams } from '../../modules/common/create-edit-popup/create-edit-popup.controller'

import { ToastHelper, ConvertHelper, LocalizationHelper, LogHelper, DataTypeGroupComponent, CommonHelper, WhereClauseHelper } from '../../helpers'

import { GridService, GRID_SERVICE_NAME } from '../grid'
import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'


//#region Public Types

export const PACKAGE_SERVICE_NAME = 'PackageService'
export const PACKAGE_SERVICE_PROVIDER_NAME = 'PackageServiceProvider'

//#endregion


export class PackageService {

    //#region Consts

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Package]' }
    public static get FILE_NAME_PREFIX(): string { return 'packages' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdPackage }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Package }

    //#endregion


    //#region Ctor

    constructor(
        private gridService: GridService,
        private userSessionService: UserSessionService
    ) { }

    //#endregion


    //#region Columns

    public getMainGridColumns(): JQueryPromise<ColumnModel[]> {
        return this.gridService.getColumnModelByCategory([
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
        ], PackageService.SIMAX_MODULE_NAME)
    }

    //#endregion


    //#region Create

    public createOpPackageFromIOpObject(sourceIOpObject: IOpObject, sourcePackage: OpPackage): OpPackage {
        if (sourceIOpObject == null) {
            return
        }

        const newPackage: OpPackage = OpPackage.fromIOpObject(sourceIOpObject)

        newPackage.idPackageStatus = Enums.PackageStatus.New

        if (newPackage.idPackage === 0) {
            newPackage.creationDate = new Date()
        }
        else {
            if (sourcePackage != null) {
                newPackage.creationDate = sourcePackage.creationDate
            }
        }

        return newPackage
    }

    public getNewPackageName(packageIndex: number): string {
        return `${this.userSessionService.getLoggedOperator().login}: ${Date.now()}${packageIndex}`
    }

    //#endregion


    //#region Delete

    public deletePackage(packageToDelete: OpPackage): JQueryPromise<void> {
        const toDelete: OpPackage = packageToDelete.clone()

        toDelete.dataList.setValue(Enums.PackageStatus.Removed, Enums.DataType.HELPER_ID_PACKAGE_STATUS)
        toDelete.idPackageStatus = Enums.PackageStatus.Removed
        toDelete.opState = Enums.OpChangeState.Modified

        return this.userSessionService
            .getDataProvider().Order
            .deleteOrder(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, PackageService.SIMAX_MODULE_NAME))
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, PackageService.SIMAX_MODULE_NAME))
                LogHelper.warn(PackageService.LOG_PREFIX, `Unable to delete order for id ${packageToDelete.idPackage}`, error)
            })
    }

    //#endregion


    //#region Get

    public getPackage(idPackage: number) {
        return this.userSessionService
            .getDataProvider().Package
            .getPackage(idPackage)
            .then((pckage: OpPackage) => {
                if (pckage != null) {
                    return pckage
                }

                return $.Deferred().reject(null).promise()
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(PackageService.LOG_PREFIX, `Unable to load package for id ${idPackage}`, error)

                return null
            })
    }

    public getGridEditData(columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string): JQueryPromise<any> {
        return this.userSessionService.getDataProvider()
            .View
            .getActiveView(Enums.ReferenceType.SerialNbr)
            .then((view: OpView) => {
                const locationTypeColumnName: string = this.getColumnName(view)
                return this.userSessionService
                    .getDataProvider().Device
                    .getDeviceFilter(columns, pageNbr, pageSize, WhereClauseHelper.appendIn(customWhereClause, locationTypeColumnName, [Enums.LocationType.DepositoryLocation, Enums.LocationType.ServiceLocation].join(', ')))
            })
    }

    private getColumnName(view: OpView): string {
        if (this.isViewCorrectlyConfigured(view)) {
            return _.find(view.columns, (e: OpViewColumn) => e.idDataType == Enums.DataType.HELPER_ID_LOCATION_TYPE).name
        } else {
            ToastHelper.showError('Bad view configuration please to contact with support')
            return ''
        }
    }

    private isViewCorrectlyConfigured(view: OpView): boolean {
        const hasDataType = (dataType: Enums.DataType): boolean => {
            return _.find(view.columns, ((column: OpViewColumn) => column.idDataType === dataType)) != null
        }

        if (hasDataType(Enums.DataType.HELPER_ID_LOCATION) && hasDataType(Enums.DataType.HELPER_ID_LOCATION_TYPE)) {
            return true
        }

        return false
    }

    public isEverythingAccepted(depoElements: OpDepositoryElement[]): boolean {
        return depoElements.some((element: OpDepositoryElement) => {
            if (element.accepted == true) {
                return false
            }
            if ((element.accepted == false || element.accepted == null) && (_.size(element.notes) == 0)) {
                return true
            }
            if ((element.accepted == false || element.accepted == null) && (_.size(element.notes) > 0)) {
                return false
            }
        })
    }

    //#endregion


    //#region Options

    public static getCreateEditOptions(userSession: UserSessionService, referenceObjectId: number = 0, referenceObject: IReferenceType & IOpObject = null): CreateEditInitParams {
        return {
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: Enums.ReferenceType.IdDistributor,
            groupReferenceValue: userSession.getLoggedOperator().idDistributor,
            idReferenceType: PackageService.REFERENCE_TYPE,
            moduleName: PackageService.SIMAX_MODULE_NAME,
            sections: null,
            useBusinessObjects: true,
            referenceObject,
            referenceObjectId,
        }
    }

    //#endregion


    //#region Parameters 

    public getBasicParametrsDataTypeGroup(): JQueryPromise<OpDataTypeInGroup[]> {
        return DataTypeGroupComponent
            .getDataTypeInGroupByModule(
                this.userSessionService.getDataProvider(),
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
                PackageService.SIMAX_MODULE_NAME
            )
    }

    //#endregion


    //#region Save


    public savePackage(packageToSave: OpPackage, isBulkSave: boolean = false): JQueryPromise<OpPackage> {
        return this.userSessionService
            .getDataProvider().Package
            .savePackage(packageToSave)
            .then((savedObject: OpPackage) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, PackageService.SIMAX_MODULE_NAME))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.packageSaved, true, 0)
                }

                return savedObject
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, PackageService.SIMAX_MODULE_NAME))
                LogHelper.warn(PackageService.LOG_PREFIX, `Unable to save package with id ${packageToSave.idDistributor}.`, error)

                return $.Deferred().reject(error).promise()
            })
    }

    //#endregion
}


export class PackageServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [GRID_SERVICE_NAME, USER_SESSION_SERVICE_NAME, function (gridService: GridService, userSessionService: UserSessionService) {
        return new PackageService(gridService, userSessionService);
    }]

    //#endregion
} 